import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import smoothscroll from 'smoothscroll-polyfill';
import * as oauth from './auth/oauth';

import store from './store/configureStore';
import OAuthRouteAppWrapper from './OAuthRouteAppWrapper';
import { addAuthInterceptor } from './api/axios';
import {
    detectDevice,
    getMainSsoTokenFromCookies,
    getOAuthIdToken,
    getSsoProductTokenFromCookies,
    getSsoProductTokenFromUrl,
    isAndroid,
    isAppBridgeAvailable,
    isIosAppBridge
} from './helpers/appBridge';
import metricApi from './api/metrics';
import { isFeatureOn } from './store/modules/content/selectors';
import { requestFeatureSwitches } from './store/modules/content/actions';
import { ENABLE_CLIENT_METRICS } from './features';
import {
    updateIdToken,
    updateProductToken
} from './store/modules/auth/actions';
import { getItem, getUserSessionId } from './helpers/sessionStorage';
import { SESSION_ITEM_ID_TOKEN } from './helpers/sessionStorageConstant';

// Do this pretty early as it's quite important
store.dispatch(requestFeatureSwitches());
const userSessionId = getUserSessionId();

const reactRoot = document.getElementById('react-root');

window.appBridge = {
    fire: (event, data) => {
        if (isFeatureOn(store.getState(), ENABLE_CLIENT_METRICS, true)) {
            metricApi.countMetric('early_appbridge', {
                event,
                data,
                userSessionId,
                userAgent: navigator.userAgent
            });
        }
    }
};

const errors = [];

window.onerror = (message, source, lineno, _, error) => {
    if (error.name === 'UserFriendlyError') {
        return;
    }
    const externalScript = message === 'Script error.' && source === '';

    if (
        isFeatureOn(store.getState(), ENABLE_CLIENT_METRICS, true) &&
        !externalScript
    ) {
        if (!errors.includes(message)) {
            errors.push(message);
            metricApi.countMetric('error', {
                message,
                source,
                lineno,
                userSessionId,
                userAgent: navigator.userAgent
            });
        }
    }
};

smoothscroll.polyfill();

if (reactRoot) {
    addAuthInterceptor(store);
    detectDevice(store);
    oauth.setDispatch(store.dispatch);

    if (isAppBridgeAvailable()) {
        const oauthIdToken =
            getOAuthIdToken() || getItem(SESSION_ITEM_ID_TOKEN);
        const ssoProductToken =
            getSsoProductTokenFromCookies() || getSsoProductTokenFromUrl();
        const mainSsoToken = getMainSsoTokenFromCookies();

        if (ssoProductToken) {
            store.dispatch(updateProductToken(ssoProductToken));
            const data = {
                token: {
                    productToken: ssoProductToken,
                    isMigrated: true
                }
            };

            // Check Main SSO Token isn't undefined, add it to the data object for Android and iOS.
            if (mainSsoToken) {
                data.token.mainSsoToken = mainSsoToken;
            }

            if (isAndroid()) {
                try {
                    const androidData = JSON.stringify(data);
                    window.Android.userLoggedIn(androidData);
                } catch (e) {
                    // Do nothing
                }
            }

            if (isIosAppBridge()) {
                try {
                    const iosData = JSON.stringify({
                        name: 'loginToken',
                        data
                    });

                    window.webkit.messageHandlers.appBridge.postMessage(
                        iosData
                    );
                } catch (e) {
                    // Do nothing
                }
            }
        }

        if (oauthIdToken) {
            store.dispatch(updateIdToken(oauthIdToken));
        }
    }

    ReactDOM.render(
        <Provider store={store}>
            <OAuthRouteAppWrapper />
        </Provider>,
        reactRoot
    );
}
